import type {
  Commitment,
  Conversation,
  ImportDraft,
  ImportedChatMessage,
  ImportedChatPreview,
  Message,
  Priority,
} from "./types";

const IOS_LINE = /^\[(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4}),\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(?:(a|p)\.?\s*m\.?)?\]\s*(.*)$/i;
const ANDROID_LINE = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4}),?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(?:(a|p)\.?\s*m\.?)?\s*[-–]\s*(.*)$/i;

const ACTION_PATTERNS = [
  /\b(enviar|mandar|cotizar|preparar|confirmar|coordinar|agendar|visitar|revisar|verificar|llamar|contactar|reparar|cambiar|instalar|entregar|informar|responder|programar|evaluar|diagnosticar)\b/i,
  /\b(necesitamos|necesito|requiero|solicitamos|favor|quedamos atentos|por favor)\b/i,
];

const URGENT_PATTERNS = [
  /\burgente\b/i,
  /\bemergencia\b/i,
  /\bsin servicio\b/i,
  /\bno funciona\b/i,
  /\bca[ií]d[oa]\b/i,
  /\baccidente\b/i,
  /\bchoque\b/i,
  /\balarma activa\b/i,
  /\binmediato\b/i,
];

const HIGH_PATTERNS = [
  /\bviernes\b/i,
  /\bmañana\b/i,
  /\bhoy\b/i,
  /\bplazo\b/i,
  /\bcotizaci[oó]n\b/i,
  /\bvisita\b/i,
  /\breclamo\b/i,
];

const TAG_RULES: Array<{ tag: string; pattern: RegExp }> = [
  { tag: "cotización", pattern: /cotiz|presupuesto|oferta/i },
  { tag: "visita", pattern: /visita|terreno|venir|asistir/i },
  { tag: "mantenimiento", pattern: /mantenci[oó]n|mantenimiento|repar|falla|diagn[oó]stico/i },
  { tag: "incidente", pattern: /incidente|choque|accidente|dañ|robo|alarma/i },
  { tag: "cámaras", pattern: /c[aá]mara|cctv|nvr|dvr/i },
  { tag: "control de acceso", pattern: /barrera|torniquete|control de acceso|lector/i },
  { tag: "comercial", pattern: /propuesta|oferta|valor|precio|contrato/i },
  { tag: "documentos", pattern: /documento|certificado|archivo|adjunto/i },
  { tag: "seguimiento", pattern: /confirmar|coordinar|agendar|quedamos atentos/i },
];

export function parseWhatsAppExport(text: string, fileName = "chat.txt"): ImportedChatPreview {
  const normalized = text
    .replace(/^\uFEFF/, "")
    .replaceAll("\u202f", " ")
    .replaceAll("\u00a0", " ")
    .replace(/\r\n?/g, "\n");
  const messages: ImportedChatMessage[] = [];
  const warnings: string[] = [];
  let current: ImportedChatMessage | null = null;

  for (const rawLine of normalized.split("\n")) {
    const line = rawLine.trimEnd();
    const match = IOS_LINE.exec(line) ?? ANDROID_LINE.exec(line);
    if (!match) {
      if (current && line.trim()) current.body = `${current.body}\n${line.trim()}`;
      continue;
    }

    const [, day, month, rawYear, rawHour, minute, second = "0", meridiem, remainder] = match;
    const date = toDate({ day, month, year: rawYear, hour: rawHour, minute, second, meridiem });
    const separator = remainder.indexOf(": ");
    const isSystem = separator < 1;
    const sender = isSystem ? "Sistema" : remainder.slice(0, separator).trim();
    const body = (isSystem ? remainder : remainder.slice(separator + 2)).trim();
    const message: ImportedChatMessage = {
      id: `import-${String(messages.length + 1).padStart(5, "0")}`,
      sender,
      body,
      sentAt: formatLocal(date),
      sentAtIso: Number.isNaN(date.getTime()) ? undefined : date.toISOString(),
      type: inferMessageType(body, isSystem),
      isSystem,
    };
    messages.push(message);
    current = message;
  }

  const participants = [...new Set(messages.filter((item) => !item.isSystem).map((item) => item.sender))];
  if (!messages.length) warnings.push("No se reconocieron mensajes. Exporta el chat sin comprimir y selecciona el archivo .txt.");
  if (messages.length > 5_000) warnings.push("El chat contiene más de 5.000 mensajes. La primera importación puede tardar algunos segundos.");
  if (participants.length < 2 && messages.length) warnings.push("Sólo se detectó un participante. Revisa que el archivo corresponda a una conversación exportada.");

  return {
    fileName,
    participants,
    messages,
    firstMessageAt: messages[0]?.sentAtIso,
    lastMessageAt: messages.at(-1)?.sentAtIso,
    warnings,
  };
}

export function buildConversationFromImport(
  preview: ImportedChatPreview,
  draft: ImportDraft,
): Conversation {
  if (!preview.messages.length) throw new Error("IMPORT_WITHOUT_MESSAGES");
  const myName = draft.myParticipantName.trim().toLocaleLowerCase("es-CL");
  const messages: Message[] = preview.messages.map((message) => ({
    id: message.id,
    sender: message.sender,
    body: message.body,
    sentAt: message.sentAt,
    sentAtIso: message.sentAtIso,
    type: message.type,
    direction:
      !message.isSystem && message.sender.trim().toLocaleLowerCase("es-CL") === myName
        ? "outbound"
        : "inbound",
  }));
  const analysis = analyzeMessages(messages, draft.assignedTo);
  const lastMessage = [...messages].reverse().find((message) => message.type !== "system") ?? messages.at(-1)!;
  const id = `import-${slug(draft.customer || preview.fileName)}-${Date.now().toString(36)}`;

  return {
    id,
    tenantId: "pgr-seguridad",
    company: draft.company.trim() || "PGR Seguridad",
    customer: draft.customer.trim() || preview.participants.find((name) => name.toLocaleLowerCase("es-CL") !== myName) || "Cliente importado",
    site: draft.site.trim() || "Sin instalación asignada",
    contactName: draft.contactName.trim() || lastMessage.sender,
    phone: draft.phone.trim() || "Sin teléfono",
    summary: analysis.summary,
    lastMessage: lastMessage.body,
    lastMessageAt: lastMessage.sentAt,
    unreadCount: lastMessage.direction === "inbound" ? 1 : 0,
    priority: analysis.priority,
    status: lastMessage.direction === "inbound" ? "open" : "waiting",
    assignedTo: draft.assignedTo.trim() || "Sin asignar",
    tags: analysis.tags,
    commitments: analysis.commitments.map((item) => ({ ...item, conversationId: id })),
    messages,
    source: "whatsapp_export",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    nextAction: analysis.commitments[0]?.description ?? "Revisar y clasificar la conversación",
    visitRequired: analysis.visitRequired,
    reportRequired: analysis.reportRequired,
  };
}

export function analyzeMessages(messages: Message[], responsible: string) {
  const usefulMessages = messages.filter((message) => message.type !== "system" && message.body.trim());
  const corpus = usefulMessages.map((message) => message.body).join(" \n");
  const tags = TAG_RULES.filter((rule) => rule.pattern.test(corpus)).map((rule) => rule.tag).slice(0, 5);
  const priority: Priority = URGENT_PATTERNS.some((pattern) => pattern.test(corpus))
    ? "urgent"
    : HIGH_PATTERNS.some((pattern) => pattern.test(corpus))
      ? "high"
      : "normal";
  const commitments = detectCommitments(usefulMessages, responsible);
  const visitRequired = /visita|terreno|venir|t[eé]cnico|inspecci[oó]n|revisar en/i.test(corpus);
  const reportRequired = /cotiz|informe|reporte|diagn[oó]stico|propuesta|presupuesto/i.test(corpus);
  const subject = tags.length ? tags.slice(0, 3).join(", ") : "un requerimiento operativo";
  const action = commitments[0]?.description;
  const summary = action
    ? `La conversación trata sobre ${subject}. El principal siguiente paso detectado es: ${lowerFirst(action)}.`
    : `La conversación trata sobre ${subject}. Aún no se detecta un compromiso explícito; conviene revisarla y definir el siguiente paso.`;

  return {
    summary,
    priority,
    tags: tags.length ? tags : ["por clasificar"],
    commitments,
    visitRequired,
    reportRequired,
  };
}

function detectCommitments(messages: Message[], responsible: string): Commitment[] {
  const seen = new Set<string>();
  const commitments: Commitment[] = [];
  for (const message of messages) {
    const normalized = message.body.replace(/\s+/g, " ").trim();
    if (!normalized || !ACTION_PATTERNS.some((pattern) => pattern.test(normalized))) continue;
    const sentence = normalized.split(/(?<=[.!?])\s+/)[0]?.slice(0, 220) ?? normalized.slice(0, 220);
    const key = sentence.toLocaleLowerCase("es-CL").replace(/[^a-záéíóúñ0-9]+/gi, " ").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    commitments.push({
      id: `task-${message.id}`,
      description: sentence.replace(/^por favor[,\s]*/i, "").replace(/^favor[,\s]*/i, ""),
      dueAt: inferDueLabel(normalized),
      responsible: responsible.trim() || "Sin asignar",
      status: "pending",
      sourceMessageId: message.id,
      createdAt: new Date().toISOString(),
    });
    if (commitments.length >= 8) break;
  }
  return commitments;
}

function inferDueLabel(value: string): string | undefined {
  if (/\bhoy\b/i.test(value)) return "Hoy";
  if (/\bmañana\b/i.test(value)) return "Mañana";
  const weekday = value.match(/\b(lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\b/i)?.[1];
  const time = value.match(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/)?.[0]?.replace(".", ":");
  if (weekday && time) return `${capitalize(weekday)} · ${time}`;
  if (weekday) return capitalize(weekday);
  if (time) return time;
  return undefined;
}

function inferMessageType(body: string, isSystem: boolean): Message["type"] {
  if (isSystem) return "system";
  const value = body.toLocaleLowerCase("es-CL");
  if (/audio|nota de voz|ptt/.test(value) && /omitid|adjunt|archivo/.test(value)) return "audio";
  if (/imagen|foto|sticker|gif/.test(value) && /omitid|adjunt|archivo/.test(value)) return "image";
  if (/documento|pdf|archivo/.test(value) && /omitid|adjunt|archivo/.test(value)) return "document";
  return "text";
}

function toDate(input: {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
  second: string;
  meridiem?: string;
}) {
  const year = Number(input.year) < 100 ? 2000 + Number(input.year) : Number(input.year);
  let hour = Number(input.hour);
  if (input.meridiem?.toLowerCase() === "p" && hour < 12) hour += 12;
  if (input.meridiem?.toLowerCase() === "a" && hour === 12) hour = 0;
  return new Date(year, Number(input.month) - 1, Number(input.day), hour, Number(input.minute), Number(input.second));
}

function formatLocal(date: Date) {
  if (Number.isNaN(date.getTime())) return "Fecha desconocida";
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function slug(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 42) || "chat";
}

function lowerFirst(value: string) {
  return value ? value[0]!.toLocaleLowerCase("es-CL") + value.slice(1) : value;
}

function capitalize(value: string) {
  return value ? value[0]!.toLocaleUpperCase("es-CL") + value.slice(1).toLocaleLowerCase("es-CL") : value;
}
